import moment from 'moment';
import { Box, Button, Column, Row, Spinner, Typography } from '@tempmee/react';
import { colors, shades, shadows } from '@tempmee/tailwind';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useParams } from 'react-router';
import history from '~/browserHistory';
import {
  ADJUST_HOURS_ACTION,
  FETCH_JOB_ACTION,
  adjustHours,
  fetchJob,
  fetchMetadata,
} from '../actions';
import { createLoadingSelector } from '../api/selectors';
import { InputSelect } from '../commonComponents'; // KEEP: has auto-scroll-to-selected behavior on open (commonComponents/InputSelect/InputSelect.tsx:83-100)
import ShowStartTime from '../commonComponents/showStartTime';
import {
  timeDifferenceCalculator,
  timeDifferenceFormValueMapping,
} from '../hooks/timeDifferenceCalculator';
import '../themes/global.scss';
import '../themes/job_summary.scss';
import {
  formatBreak,
  isEqualHour,
  pickerResultToTime,
  populateInitialFormValues,
} from './adjustments/utils';
import AdjustmentItem from './JobSummary/AdjustmentItem';
import ProfileSummary from './JobSummary/ProfileSummary';
import ConfirmAdjustmentPopup from './Request/confirmAdjustmentPopup';
import { useSkipJobAdjustment } from '~/domain/JobAdjustment';
import { useToggle } from '~/hooks';
import { SelectMealBreakTimePopup } from './Request/SelectMealBreakTimePopup';

// Card elevation matching legacy boxShadow.
const CARD_SHADOW = shadows['elevation-4'];

export const JobSummaryAdjustment = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const jobId = params?.jobId;
  const [showConfirmAdjustmentPopupModal, setShowConfirmAdjustmentPopupModal] = useState(false);
  const [isPickerShowStartTime, setIsPickerShowStartTime] = useState(false);
  const [isPickerShowEndTime, setIsPickerShowEndTime] = useState(false);
  const [isPickerShowBreakStart, setIsPickerShowBreakStart] = useState(false);
  const [isPickerShowBreakEnd, setIsPickerShowBreakEnd] = useState(false);
  const [isPickerMealBreakOpen, controlsPickerMealBreak] = useToggle(false);
  const [originalHours, setOriginalHours] = useState<any>({});
  const [form, setForm] = useState<any>({
    start: null,
    end: null,
    break: null,
    breakStart: null,
    breakEnd: null,
    breakPaid: null,
    message: '',
    adjustmentReason: null,
  });

  const job = useAppSelector((state) => state.job.job);
  const isNewBreakFlowEnabled = job?.workerClassification === 'employee';
  const isArrivalAdjusted = !isEqualHour(originalHours.start, form.start);
  const isDepartureAdjusted = !isEqualHour(originalHours.end, form.end);
  const isBreakStartAdjusted = !isEqualHour(originalHours.breakStart, form.breakStart);
  const isBreakEndAdjusted = !isEqualHour(originalHours.breakEnd, form.breakEnd);
  const isBreakAdjusted = originalHours.break !== form.break;
  const isAdjusted = isArrivalAdjusted || isDepartureAdjusted || isBreakAdjusted;

  const actions = [FETCH_JOB_ACTION, ADJUST_HOURS_ACTION];
  const loadingSelector = createLoadingSelector(actions);
  const isLoading = useAppSelector((state) => loadingSelector(state));

  const adjustmentReasonOptions = useAppSelector((state) =>
    (state.user?.allMetadata as any)?.adjustmentReasons?.map((it: any) => ({
      title: it.title,
      value: it.code,
    })),
  );

  const isInvalidForm = useMemo(() => {
    return (
      !form.start ||
      !form.end ||
      (form.breakStart && !form.breakEnd) ||
      (form.breakEnd && !form.breakStart) ||
      (isAdjusted && !form.adjustmentReason) ||
      (isAdjusted && form.adjustmentReason === 'other' && !form.message)
    );
  }, [form, isAdjusted]);

  const { mutate: skipJobAdjustment, isPending: isSkippingJobAdjustment } = useSkipJobAdjustment({
    onSuccess: () => {
      history.push(`/dashboard/job/${jobId}/feedback-rating`);
    },
  });

  useEffect(() => {
    if (!job || jobId !== job.id) {
      dispatch(fetchJob({ jobId }));
    }
  }, [jobId, dispatch, job]);

  useEffect(() => {
    dispatch(fetchMetadata());
  }, [dispatch]);

  useEffect(() => {
    if (job && job.isFeedbackSubmited) {
      history.replace(`/dashboard/job/${job.id}/details`);
    }

    if (job) {
      const {
        originalStartHour,
        originalEndHour,
        originalBreakTime,
        adjustedStartHour,
        adjustedEndHour,
        adjustedBreakTime,
        breakStart,
        breakEnd,
      } = populateInitialFormValues(job, isNewBreakFlowEnabled);

      setOriginalHours({
        start: originalStartHour,
        end: originalEndHour,
        break: originalBreakTime,
        breakStart,
        breakEnd,
        breakPaid: job?.is_lunch_break_paid,
      });

      setForm({
        start: adjustedStartHour,
        end: adjustedEndHour,
        break: adjustedBreakTime,
        breakStart,
        breakEnd,
        breakPaid: job?.is_lunch_break_paid,
        message: '',
      });
    }
  }, [job]);

  const handleBreakChange = (newFormValue: any) => {
    const { breakStart, breakEnd } = newFormValue;
    if (breakStart && breakEnd) {
      const breakTime = moment(breakEnd).diff(moment(breakStart), 'minutes');
      setForm({ ...newFormValue, break: String(breakTime) });
    } else {
      setForm({ ...newFormValue, break: null });
    }
  };

  const submitAdjustment = () => {
    const data = {
      break: form.break,
      localDate: job.local_date,
      localStart: moment(form.start).format('hh:mm a').toString(),
      localEnd: moment(form.end).format('hh:mm a').toString(),
      message: form.message,
      adjustmentReason: form.adjustmentReason,
    };
    dispatch(adjustHours({ data, jobId, navigateToRating: true }));
  };

  if (!job || isLoading) {
    return (
      <Box style={{ backgroundColor: colors.neutral[100], flex: 1 }}>
        <Box style={{ height: '100%', backgroundColor: shades[0] }}>
          <Row align="center" justify="center" style={{ width: '100%', flex: 1, minHeight: 300 }}>
            <Spinner />
          </Row>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box style={{ width: '100%' }}>
        <ProfileSummary
          title={job?.isOnsiteInterview ? 'Interview Summary' : 'Shift Summary'}
          job={job}
        />

        <Column style={{ alignItems: 'center', margin: '-1px 20px 10px 20px' }}>
          <Box style={{ width: '100%' }}>
            <Box
              style={{
                backgroundColor: shades[0],
                paddingBlock: 62,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: CARD_SHADOW,
              }}
            >
              <Column
                style={{
                  maxWidth: 658,
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography bold style={{ fontSize: 22 }}>
                  {job?.isOnsiteInterview ? 'Interview Details' : 'Shift Details'}
                </Typography>

                <Typography italic color={colors.neutral[500]} style={{ textAlign: 'center' }}>
                  If times have changed, edit the adjustments below.
                  <br />
                  If there are not changes to be submitted, click &quot;Skip&quot;
                </Typography>

                <Typography style={{ marginTop: 50, marginBottom: 35 }}>
                  <Typography as="span" bold>Date:</Typography>{' '}{job.local_date}
                </Typography>

                <Box style={{ width: '90%' }}>
                  <AdjustmentItem
                    firstRow
                    label="Arrival:"
                    adjustedLabel="Adjusted Arrival:"
                    defaultValue={moment(originalHours.start).format('hh:mm a')}
                    value={moment(form.start).format('hh:mm a')}
                    isAdjusted={isArrivalAdjusted}
                    onEditClicks={() => setIsPickerShowStartTime(true)}
                  />
                  <AdjustmentItem
                    label="Departure:"
                    adjustedLabel="Adjusted Departure:"
                    defaultValue={moment(originalHours.end).format('hh:mm a')}
                    value={moment(form.end).format('hh:mm a')}
                    isAdjusted={isDepartureAdjusted}
                    onEditClicks={() => setIsPickerShowEndTime(true)}
                  />
                  {isNewBreakFlowEnabled && (
                    <AdjustmentItem
                      label="Meal Break Start:"
                      adjustedLabel="Adjusted Meal Break Start:"
                      defaultValue={
                        !originalHours.breakStart
                          ? '--'
                          : moment(originalHours.breakStart).format('hh:mm a')
                      }
                      value={moment(form.breakStart).format('hh:mm a')}
                      isAdjusted={isBreakStartAdjusted}
                      onEditClicks={() => setIsPickerShowBreakStart(true)}
                    />
                  )}
                  {isNewBreakFlowEnabled && (
                    <AdjustmentItem
                      label="Meal Break End:"
                      adjustedLabel="Adjusted Meal Break End:"
                      defaultValue={
                        !originalHours.breakEnd
                          ? '--'
                          : moment(originalHours.breakEnd).format('hh:mm a')
                      }
                      value={moment(form.breakEnd).format('hh:mm a')}
                      isAdjusted={isBreakEndAdjusted}
                      onEditClicks={() => setIsPickerShowBreakEnd(true)}
                    />
                  )}
                  {isNewBreakFlowEnabled ? (
                    <AdjustmentItem
                      label="Meal Break Time:"
                      adjustedLabel="Adjusted Meal Break Time:"
                      defaultValue={formatBreak(form.break, form.breakPaid)}
                      disabled
                    />
                  ) : (
                    <AdjustmentItem
                      label="Meal Break:"
                      adjustedLabel="Adjusted Meal Break:"
                      defaultValue={formatBreak(originalHours.break, originalHours.breakPaid)}
                      value={formatBreak(form.break, form.breakPaid)}
                      isAdjusted={isBreakAdjusted}
                      onEditClicks={controlsPickerMealBreak.setOn}
                    />
                  )}
                </Box>

                {isAdjusted ? (
                  <>
                    <Column gap={8} style={{ marginTop: 70 }}>
                      <Box style={{ width: 468 }}>
                        <Row align="center" gap={1} style={{ marginBottom: 8 }}>
                          <Typography bold>Reason for Adjustment</Typography>
                          <Typography bold color={colors.error[400]}>*</Typography>
                        </Row>
                        <InputSelect
                          isSelectVisible={false}
                          options={adjustmentReasonOptions}
                          placeholder="Select Reason"
                          value={form.adjustmentReason}
                          setValue={(value: string) =>
                            setForm({ ...form, adjustmentReason: value })
                          }
                        />
                      </Box>
                      <Box style={{ width: 468 }}>
                        <Row align="center" gap={1} style={{ marginBottom: 8 }}>
                          <Typography bold>Provide Details</Typography>
                          {form.adjustmentReason !== 'other' && (
                            <Typography color={colors.neutral[500]}>(Optional)</Typography>
                          )}
                        </Row>
                        <textarea
                          className="textarea-style"
                          style={{ width: 'auto' }}
                          placeholder="Enter Details"
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                        />
                      </Box>
                    </Column>
                    <Box style={{ marginTop: 42 }}>
                      <Button
                        variant="primary"
                        disabled={isInvalidForm}
                        onClick={() => setShowConfirmAdjustmentPopupModal(true)}
                      >
                        Submit & Continue
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box style={{ marginTop: 70 }}>
                    <Button
                      variant="primary"
                      isLoading={isSkippingJobAdjustment}
                      data-cy="job-summary-skip-button"
                      onClick={() => skipJobAdjustment(jobId)}
                    >
                      Skip
                    </Button>
                  </Box>
                )}
              </Column>
            </Box>
          </Box>
        </Column>
      </Box>

      {isPickerShowStartTime && (
        <ShowStartTime
          content="start_time"
          result={(result: string) =>
            setForm({ ...form, start: pickerResultToTime(job, result?.toLowerCase()) })
          }
          closeModal={() => setIsPickerShowStartTime(false)}
          defaultStartTime={form.start}
          defaultEndTime={form.end}
          minuteInterval={isNewBreakFlowEnabled ? 1 : 15}
        />
      )}
      {isPickerShowEndTime && (
        <ShowStartTime
          content="end_time"
          result={(result: string) =>
            setForm({ ...form, end: pickerResultToTime(job, result?.toLowerCase()) })
          }
          closeModal={() => setIsPickerShowEndTime(false)}
          defaultStartTime={form.start}
          defaultEndTime={form.end}
          minuteInterval={isNewBreakFlowEnabled ? 1 : 15}
        />
      )}
      {isPickerShowBreakStart && (
        <ShowStartTime
          content="start_time"
          result={(result: string) =>
            handleBreakChange({
              ...form,
              breakStart: pickerResultToTime(job, result?.toLowerCase()),
            })
          }
          closeModal={() => setIsPickerShowBreakStart(false)}
          defaultStartTime={form.breakStart || form.start}
          defaultEndTime={form.breakEnd || form.end}
          minuteInterval={1}
        />
      )}
      {isPickerShowBreakEnd && (
        <ShowStartTime
          content="end_time"
          result={(result: string) =>
            handleBreakChange({
              ...form,
              breakEnd: pickerResultToTime(job, result?.toLowerCase()),
            })
          }
          closeModal={() => setIsPickerShowBreakEnd(false)}
          defaultStartTime={form.breakStart || form.start}
          defaultEndTime={form.breakEnd || form.end}
          minuteInterval={1}
        />
      )}
      <SelectMealBreakTimePopup
        isOpen={isPickerMealBreakOpen}
        toggleControl={controlsPickerMealBreak}
        onSubmit={(formData: any) =>
          setForm({ ...form, break: formData.break, breakPaid: formData.isMealBreakPaid })
        }
      />
      {showConfirmAdjustmentPopupModal ? (
        <ConfirmAdjustmentPopup
          closePopupClicked={() => setShowConfirmAdjustmentPopupModal(false)}
          btnConfirmClicked={() => submitAdjustment()}
          adjustedHours={`${moment(form.start).format('hh:mm a')} - ${moment(form.end).format('hh:mm a')}`}
          adjustedBreak={`${form.break} minutes(${form.breakPaid ? 'Paid' : 'Unpaid'})`}
          totalDifferenceHours={(() => {
            const {
              originalStartTime,
              originalEndTime,
              adjustedStartTime,
              adjustedEndTime,
              originalLunchBreakTime,
              adjustedLunchBreakTime,
            } = timeDifferenceFormValueMapping(job, form);

            return timeDifferenceCalculator(
              originalStartTime,
              originalEndTime,
              adjustedStartTime,
              adjustedEndTime,
              originalLunchBreakTime,
              adjustedLunchBreakTime,
            );
          })()}
        />
      ) : null}
    </>
  );
};
